import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import HelpTrigger from '../../../help/components/HelpTrigger';
import { INITIATIVE_STATUSES, statusLabel, statusVariant } from '../../../constants/ballotInitiatives';
import { formatDate } from '../../lib/format';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

export interface StatusEvent {
  id: string;
  status: string;
  occurred_on: string;
  note: string | null;
  source_url: string | null;
}

interface Props {
  initiativeId: string;
  events: StatusEvent[];
  signaturesRequired: number | null;
  signaturesSubmitted: number | null;
  signaturesValid: number | null;
}

const EMPTY = { status: '', occurred_on: '', note: '', source_url: '' };

const percentOfRequired = (valid: number | null, required: number | null): string | null => {
  if (valid == null || !required) return null;
  return `${Math.round((valid / required) * 1000) / 10}% of required`;
};

/**
 * Path to the Ballot — a measure's dated lifecycle events.
 *
 * The measure's own `status` column holds only where it is now; each change
 * overwrites the last. These rows are the record that survives, which is what a
 * client brief is built from.
 *
 * Carries the one contextual help trigger in this feature: the pre-ballot /
 * ballot-visible distinction is invisible state from here — an admin setting a
 * measure to "circulating" cannot see the citizen ballot it is being kept off.
 */
const InitiativeLifecycleCard: React.FC<Props> = ({
  initiativeId, events, signaturesRequired, signaturesSubmitted, signaturesValid,
}) => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StatusEvent | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState<StatusEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => invalidateInitiativeQueries(queryClient, initiativeId);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, occurred_on: new Date().toISOString().slice(0, 10) });
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (event: StatusEvent) => {
    setEditing(event);
    setForm({
      status: event.status,
      occurred_on: event.occurred_on ? event.occurred_on.slice(0, 10) : '',
      note: event.note ?? '',
      source_url: event.source_url ?? '',
    });
    setError(null);
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        status: form.status,
        occurred_on: form.occurred_on,
        note: form.note.trim() || null,
        source_url: form.source_url.trim() || null,
      };
      return editing
        ? api.put(`/admin/ballot-initiatives/${initiativeId}/status-history/${editing.id}`, body)
        : api.post(`/admin/ballot-initiatives/${initiativeId}/status-history`, body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Event updated' : 'Event added');
      setModalOpen(false);
      refresh();
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save the event')),
  });

  const deleteMutation = useMutation({
    mutationFn: (event: StatusEvent) =>
      api.delete(`/admin/ballot-initiatives/${initiativeId}/status-history/${event.id}`),
    onSuccess: () => {
      toast.success('Event removed');
      setConfirmDelete(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to remove the event')),
  });

  const pct = percentOfRequired(signaturesValid, signaturesRequired);

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
            Path to the Ballot
          </h3>
          <HelpTrigger contentKey="initiative-lifecycle" size="sm" />
        </div>
        <Button variant="ghost" size="sm" icon={Plus} onClick={openAdd}>
          Add Event
        </Button>
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          No lifecycle events recorded yet.
        </p>
      ) : (
        <ol className="space-y-3">
          {events.map((event) => (
            <li key={event.id} className="flex items-start justify-between gap-3 border-l-2 border-border-light pl-3 dark:border-border-dark">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip label={statusLabel(event.status)} variant={statusVariant(event.status)} size="sm" />
                  <span className="text-sm tabular-nums text-text-secondary dark:text-text-dark-secondary">
                    {formatDate(event.occurred_on)}
                  </span>
                </div>
                {event.note && (
                  <p className="mt-1 text-sm text-text-primary dark:text-text-dark-primary">{event.note}</p>
                )}
                {event.source_url && (
                  <a
                    href={event.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-text-primary hover:underline"
                  >
                    Source <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit event" onClick={() => openEdit(event)} />
                <Button variant="ghost" size="sm" icon={Trash2} ariaLabel="Remove event" onClick={() => setConfirmDelete(event)} />
              </div>
            </li>
          ))}
        </ol>
      )}

      {(signaturesRequired != null || signaturesSubmitted != null || signaturesValid != null) && (
        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-border-light pt-3 text-sm dark:border-border-dark">
          <div>
            <dt className="text-text-tertiary dark:text-text-dark-tertiary">Required</dt>
            <dd className="tabular-nums text-text-primary dark:text-text-dark-primary">
              {signaturesRequired?.toLocaleString() ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary dark:text-text-dark-tertiary">Submitted</dt>
            <dd className="tabular-nums text-text-primary dark:text-text-dark-primary">
              {signaturesSubmitted?.toLocaleString() ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary dark:text-text-dark-tertiary">Valid</dt>
            <dd className="tabular-nums text-text-primary dark:text-text-dark-primary">
              {signaturesValid?.toLocaleString() ?? '—'}
              {pct && (
                <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">({pct})</span>
              )}
            </dd>
          </div>
        </dl>
      )}

      <AdminModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Lifecycle Event' : 'Add Lifecycle Event'}
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => saveMutation.mutate()} disabled={!form.status || !form.occurred_on || saveMutation.isPending}>
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </>}
      >
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}
          <div>
            <label htmlFor="event-status" className={labelClasses}>Status *</label>
            <select
              id="event-status"
              value={form.status}
              onChange={(e) => setForm((c) => ({ ...c, status: e.target.value }))}
              className={inputClasses}
            >
              <option value="">Select a status…</option>
              {INITIATIVE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="event-date" className={labelClasses}>Date *</label>
            <input
              id="event-date"
              type="date"
              value={form.occurred_on}
              onChange={(e) => setForm((c) => ({ ...c, occurred_on: e.target.value }))}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="event-note" className={labelClasses}>Note</label>
            <textarea
              id="event-note"
              rows={2}
              value={form.note}
              onChange={(e) => setForm((c) => ({ ...c, note: e.target.value }))}
              className={inputClasses}
              placeholder="e.g. Title Board set the title; rehearing motion denied"
            />
          </div>
          <div>
            <label htmlFor="event-source" className={labelClasses}>Source URL</label>
            <input
              id="event-source"
              type="url"
              value={form.source_url}
              onChange={(e) => setForm((c) => ({ ...c, source_url: e.target.value }))}
              className={inputClasses}
              placeholder="https://www.sos.state.co.us/…"
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={confirmDelete !== null}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete)}
        title="Remove this event?"
        message={
          confirmDelete
            ? `${statusLabel(confirmDelete.status)} on ${formatDate(confirmDelete.occurred_on)} will be removed from the timeline.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default InitiativeLifecycleCard;
